import { useCallback } from 'react';
import { useTrack } from '@frameio/shared/segment/hooks';
import { Box, MenuGroup, MenuRadio, Tooltip } from '@frameio/vapor';
import { useUpdateCollectionPrivacy } from '../../hooks';
import type { CollectionPrivacySelector } from './CollectionPrivacySelector';
import { Icon } from './constants';

const ICON_SIZE = 24;

type CollectionPrivacySelectorMenuProps = {
  withDetails?: boolean;
} & Pick<
  React.ComponentProps<typeof CollectionPrivacySelector>,
  'privateText' | 'publicText' | 'isPrivate' | 'collectionId'
>;

export const CollectionPrivacySelectorMenu = ({
  isPrivate,
  collectionId,
  withDetails = true,
  privateText,
  publicText,
}: CollectionPrivacySelectorMenuProps) => {
  const { updateCollectionPrivacy } = useUpdateCollectionPrivacy();
  const track = useTrack();
  const setCollectionPrivacyValue = useCallback(
    (value: boolean) => {
      track(
        'collection-privacy-updated',
        {
          collection_id: collectionId,
          is_private: value,
        },
        { logToDatadog: true }
      );
      updateCollectionPrivacy({ collectionId, isPrivate: value });
    },
    [collectionId, track, updateCollectionPrivacy]
  );

  return (
    <Box style={{ width: '310px' }}>
      <MenuGroup>
        <Tooltip disabled={withDetails} title={privateText.details}>
          <MenuRadio
            alignIconBefore="start"
            checked={isPrivate}
            details={withDetails ? privateText.details : undefined}
            iconBefore={<Icon.Private size={ICON_SIZE} />}
            name="private"
            onSelect={() => setCollectionPrivacyValue(true)}
          >
            {privateText.label}
          </MenuRadio>
        </Tooltip>
        <Tooltip disabled={withDetails} title={publicText.details}>
          <MenuRadio
            alignIconBefore="start"
            checked={!isPrivate}
            details={withDetails ? publicText.details : undefined}
            iconBefore={<Icon.Public size={ICON_SIZE} />}
            name="public"
            onSelect={() => setCollectionPrivacyValue(false)}
          >
            {publicText.label}
          </MenuRadio>
        </Tooltip>
      </MenuGroup>
    </Box>
  );
};
